/**
 * Autosave: when to write, where, and what to say when it does not work.
 *
 * The editor supplies one function — `snapshot()` — and this owns everything
 * after it: debouncing, the local write, the cloud upload, the revision, and
 * the reporting. That keeps the boundary where it already is. The editor never
 * imports `src/api`, and this never touches a canvas.
 *
 * **Local always succeeds first.** The cloud copy is the shared one; the local
 * copy is the durable one. If the upload fails for any reason the user has
 * still not lost anything, which is why every message here can honestly say so.
 *
 * Pure scheduling and error mapping, with I/O injected — so all of it is
 * testable in the node suite, which is where the decisions actually live.
 */
import { packProject } from "../../shared/pack";
import { ApiError } from "../api/client";
import type { SaveResult } from "../api/projects";
import type { SaveState } from "./save-state";

export interface Snapshot {
  project: import("../../shared/format").Project;
  /** Keyed `<layerId>.png`; written into `layers/` by packProject. */
  blobs: Record<string, Uint8Array>;
  thumb?: Uint8Array;
}

export interface SaveDeps {
  /** Built by the editor from its runtime layer stack. */
  snapshot: () => Promise<Snapshot>;
  /** Durable copy. Must resolve even when signed out. */
  saveLocal: (id: string, title: string, bytes: Uint8Array, thumb?: Uint8Array) => Promise<void>;
  /** Shared copy. Absent when signed out — not an error, just not applicable. */
  uploadCloud?: (projectId: string, bytes: Uint8Array, baseRevision: number) => Promise<SaveResult>;
  onState: (state: SaveState) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface SaveControllerOptions {
  localId: string;
  /** Null until the project has been claimed by an account. */
  cloudId?: string | null;
  /** Revision the cloud copy is at. Bumped by every accepted upload. */
  revision?: number;
  /**
   * Quiet period after the last edit. Three seconds, not three hundred
   * milliseconds: `SAVE_LIMITER` allows 20 uploads a minute, so an autosave
   * firing more often than every 3s is throttled by our own guard.
   */
  idleMs?: number;
  /** Hard ceiling during continuous work, so a long session still persists. */
  maxWaitMs?: number;
}

const DEFAULT_IDLE_MS = 3_000;
const DEFAULT_MAX_WAIT_MS = 60_000;

export interface SaveController {
  /** Call after any change. Cheap; coalesces. */
  touch(): void;
  /** Write now — visibility change, unload, ⌘S. */
  flush(): Promise<void>;
  /** The project now lives in an account. */
  attachCloud(projectId: string, revision: number): void;
  /**
   * Stop uploading — the account this was attached to is no longer the one
   * signed in. Sign-out, or a different person on a shared device.
   *
   * Local saving continues untouched, which is the whole point: detaching is
   * not an error and must not look like one.
   */
  detachCloud(): void;
  /**
   * Write to a different local design from now on.
   *
   * For when the one being edited stops existing underneath us — deleted in
   * another tab, or an archive that would not open. The alternative is writing
   * to an id the user has deleted, which recreates it.
   */
  retarget(localId: string): void;
  state(): SaveState;
  destroy(): void;
}

export function createSaveController(
  deps: SaveDeps,
  options: SaveControllerOptions,
): SaveController {
  const idleMs = options.idleMs ?? DEFAULT_IDLE_MS;
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));

  let localId = options.localId;
  let cloudId = options.cloudId ?? null;
  let revision = options.revision ?? 0;
  let current: SaveState = { kind: "idle" };

  let timer: unknown = null;
  /** When the first edit of this burst landed, for the hard ceiling. */
  let burstStartedAt: number | null = null;
  let dirty = false;
  let writing = false;
  /** An edit arrived mid-write; the bytes in flight are already stale. */
  let dirtyDuringWrite = false;

  function report(state: SaveState) {
    current = state;
    deps.onState(state);
  }

  function cancelTimer() {
    if (timer !== null) clearTimer(timer);
    timer = null;
  }

  function schedule() {
    cancelTimer();
    const waited = burstStartedAt === null ? 0 : now() - burstStartedAt;
    // Whichever comes first: quiet for idleMs, or the burst hitting its ceiling.
    const delay = Math.max(0, Math.min(idleMs, maxWaitMs - waited));
    timer = setTimer(() => {
      void write();
    }, delay);
  }

  function touch() {
    dirty = true;
    if (writing) dirtyDuringWrite = true;
    if (burstStartedAt === null) burstStartedAt = now();
    schedule();
  }

  async function write(): Promise<void> {
    cancelTimer();
    if (!dirty || writing) return;

    writing = true;
    dirtyDuringWrite = false;
    report({ kind: "saving" });

    try {
      const snap = await deps.snapshot();
      const bytes = packProject(snap.project, { blobs: snap.blobs, thumb: snap.thumb });

      // Durable copy first. Everything after this can fail without costing the
      // user anything, and the messages below depend on that being true.
      await deps.saveLocal(localId, snap.project.manifest.title, bytes, snap.thumb);

      if (!deps.uploadCloud || !cloudId) {
        report({ kind: "local", reason: "signed-out" });
        settle();
        return;
      }

      try {
        const result = await deps.uploadCloud(cloudId, bytes, revision);
        revision = result.revision;
        report({ kind: "saved", at: now() });
      } catch (err) {
        report(cloudFailure(err));
      }
      settle();
    } catch (err) {
      // The local write itself failed — private mode, quota, blocked storage.
      // This is the only branch where work is genuinely at risk.
      report({
        kind: "error",
        message:
          err instanceof ApiError
            ? err.message
            : "This browser is not letting us save on this device. Export your work to keep it.",
      });
      writing = false;
      // Deliberately leaves `dirty` set: the next touch retries.
    }
  }

  function settle() {
    writing = false;
    if (dirtyDuringWrite) {
      // Edits landed while we were packing, so what we wrote is already behind.
      dirty = true;
      dirtyDuringWrite = false;
      burstStartedAt = now();
      schedule();
    } else {
      dirty = false;
      burstStartedAt = null;
    }
  }

  return {
    touch,
    flush: () => write(),
    attachCloud(projectId, nextRevision) {
      cloudId = projectId;
      revision = nextRevision;
    },
    retarget(nextLocalId) {
      localId = nextLocalId;
    },
    detachCloud() {
      cloudId = null;
      // Reset too: a revision is only meaningful against the project it came
      // from, and carrying it to the next one is how a first upload arrives
      // pre-conflicted.
      revision = 0;
    },
    state: () => current,
    destroy: cancelTimer,
  };
}

/**
 * What a failed upload means, given the local copy already succeeded.
 *
 * Exported because this mapping is the part worth testing: each of these is a
 * different thing that happened, and collapsing them into "save failed" is how
 * a user concludes their work is gone when it is sitting on their disk.
 */
export function cloudFailure(err: unknown): SaveState {
  if (!(err instanceof ApiError)) {
    return { kind: "local", reason: "offline" };
  }
  if (err.isOffline || err.code === "timeout") {
    return { kind: "local", reason: "offline" };
  }
  if (err.isUnauthenticated) {
    return { kind: "local", reason: "signed-out" };
  }
  if (err.isConflict) {
    // Stop. Retrying with a bumped revision is the same as having no check.
    return { kind: "conflict" };
  }
  if (err.code === "project_read_only") {
    return { kind: "read-only" };
  }
  if (err.code === "storage_exceeded" || err.code === "quota_exceeded") {
    return { kind: "error", message: "Your plan is full, so this is saved on this device only." };
  }
  if (err.isRateLimited) {
    return { kind: "local", reason: "offline" };
  }
  return { kind: "local", reason: "offline" };
}
